import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Heart, MapPin } from 'lucide-react';
import { Chalet } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface ChaletCardProps {
  chalet: Chalet;
  variant?: 'vertical' | 'horizontal';
}

export const ChaletCard: React.FC<ChaletCardProps> = ({ chalet, variant = 'vertical' }) => {
  const { t } = useLanguage();
  const isHorizontal = variant === 'horizontal';

  // Mapping for translated categories if needed, otherwise fallback to db value
  const displayCategory = t(`cat.${chalet.category}`) === `cat.${chalet.category}` ? chalet.category : t(`cat.${chalet.category}`);

  return (
    <Link to={`/chalet/${chalet.id}`} className="group block h-full">
      <div className={`relative h-full flex ${isHorizontal ? 'flex-col md:flex-row gap-6 md:gap-8 items-stretch' : 'flex-col'} transition-transform duration-500 hover:-translate-y-2`}>
        
        {/* Image Container */}
        <div className={`relative overflow-hidden rounded-[2rem] shadow-sm group-hover:shadow-2xl transition-all duration-500 flex-shrink-0 ${isHorizontal ? 'w-full md:w-2/5 aspect-[4/3] md:aspect-auto' : 'aspect-[10/9] w-full'}`}>
          <img 
            src={chalet.images[0]} 
            alt={chalet.title} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          
          {/* Overlay Gradient for depth */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
          
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-bold text-brand-900 shadow-lg tracking-wide uppercase border border-white/50">
            {displayCategory}
          </div>

          {/* Price Tag Overlay - Always visible on image now */}
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-md px-5 py-2.5 rounded-2xl shadow-xl border border-white/50 flex flex-col items-start transition-transform duration-300 group-hover:scale-105 z-20">
              <div className="flex items-baseline gap-1">
                 <span className="text-[11px] text-stone-500 font-bold uppercase tracking-wider">v.a.</span>
                 <span className="font-serif text-2xl font-bold text-brand-900">€{chalet.pricePerNight}</span>
                <span className="text-[11px] text-stone-500 font-bold uppercase tracking-wider">/ {t('card.night')}</span>
              </div>
          </div>
        </div>

        {/* Content */}
        <div className={`mt-5 px-2 ${isHorizontal ? 'mt-0 py-4 flex-grow flex flex-col' : ''}`}>
          
          {/* Header Row: Title */}
          <div className="flex justify-between items-start mb-2">
            <h3 className={`font-serif font-bold text-brand-900 leading-tight group-hover:text-brand-600 transition-colors ${isHorizontal ? 'text-2xl' : 'text-xl'}`}>
              {chalet.title}
            </h3>
            
            {/* Rating only on right for vertical layout, for horizontal it is below title */}
            {!isHorizontal && (
               <div className="flex items-center gap-1.5 bg-brand-50 px-2 py-1 rounded-lg flex-shrink-0">
                  <Star size={14} className="fill-brand-900 text-brand-900" />
                  <span className="text-sm font-bold text-brand-900">{chalet.rating}</span>
               </div>
            )}
          </div>
          
          {/* Horizontal: Location & Rating directly under title */}
          {isHorizontal && (
             <div className="flex items-center gap-4 mb-5 text-sm">
                <span className="flex items-center gap-1.5 text-stone-600 font-medium bg-brand-50 px-3 py-1 rounded-full">
                    <MapPin size={14} className="text-brand-600"/> 
                    {chalet.location}, {chalet.province}
                </span>
				{/*
                <span className="flex items-center gap-1 text-brand-900 font-bold">
                     <Star size={14} className="fill-brand-900" />
                     {chalet.rating}
                </span>*/}
             </div>
          )}

          {/* Description */}
          {isHorizontal && (
             <p className="text-stone-500 mb-6 line-clamp-2 md:line-clamp-3 leading-relaxed">
                 {chalet.description}
             </p>
          )}
          
          {/* Footer */}
          <div className="flex items-center justify-between mt-auto">
             {!isHorizontal && (
                <p className="text-stone-500 text-sm font-medium">
                   {chalet.location}, {chalet.province}
                </p>
             )}
             <span className={`text-sm font-bold text-brand-600 transition-all duration-300 ${isHorizontal ? 'ml-auto bg-white border border-brand-200 px-4 py-2 rounded-full hover:bg-brand-50 shadow-sm' : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'}`}>
               {t('card.details')} &rarr;
             </span>
          </div>
        </div>
      </div>
    </Link>
  );
};